import { SMOOTH_PRIORS_LAMBDA } from './constants';

const solveLinearSystem = (matrix: number[][], rhs: number[]): number[] => {
  const n = rhs.length;
  const augmented = matrix.map((row, rowIndex) => [...row, rhs[rowIndex]]);

  for (let pivot = 0; pivot < n; pivot += 1) {
    let maxRow = pivot;
    for (let row = pivot + 1; row < n; row += 1) {
      if (
        Math.abs(augmented[row][pivot]) > Math.abs(augmented[maxRow][pivot])
      ) {
        maxRow = row;
      }
    }
    [augmented[pivot], augmented[maxRow]] = [
      augmented[maxRow],
      augmented[pivot],
    ];

    const pivotValue = augmented[pivot][pivot] || 1e-12;
    for (let col = pivot; col <= n; col += 1) {
      augmented[pivot][col] /= pivotValue;
    }

    for (let row = 0; row < n; row += 1) {
      if (row === pivot) continue;
      const factor = augmented[row][pivot];
      for (let col = pivot; col <= n; col += 1) {
        augmented[row][col] -= factor * augmented[pivot][col];
      }
    }
  }

  return augmented.map(row => row[n]);
};

/**
 * Smoothness priors detrend (Tarvainen-style): trend = argmin ||y - t||^2 + lambda^2 ||D2 t||^2.
 */
export const smoothPriorsDetrend = (
  values: number[],
  lambda = SMOOTH_PRIORS_LAMBDA
): number[] => {
  const n = values.length;
  if (n <= 2) return values.map(() => 0);

  const lambdaSq = lambda * lambda;
  const matrix = Array.from({ length: n }, (_, row) =>
    Array.from({ length: n }, (_, col) => (row === col ? 1 : 0))
  );

  for (let row = 0; row < n - 2; row += 1) {
    const coeffs = [1, -2, 1];
    for (let i = 0; i < 3; i += 1) {
      for (let j = 0; j < 3; j += 1) {
        matrix[row + i][row + j] += lambdaSq * coeffs[i] * coeffs[j];
      }
    }
  }

  const trend = solveLinearSystem(matrix, values);
  return values.map((value, index) => value - trend[index]);
};
